import { useState } from "react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Avatar from "@/components/ui/Avatar";
import EmptyState from "@/components/ui/EmptyState";
import { ButtonLink, ButtonAnchor } from "@/components/ui/Button";
import { formatEventDateTime } from "@/utils/formatEventDate";
import type {
  CheckInCall,
  CheckInCallAnswer,
} from "@/interfaces/check_in_call";

function AnswerRow({ answer }: { answer: CheckInCallAnswer }) {
  return (
    <div className="border-line/20 flex items-start gap-3 border-b py-2.5 last:border-b-0">
      <Avatar
        src={answer.user.avatar}
        name={answer.user.display_name ?? answer.user.username}
        seed={answer.email}
        size="sm"
      />

      <div className="min-w-0 flex-1">
        <p className="text-body text-ink truncate font-bold">
          {answer.user.display_name ?? answer.user.username}
          {answer.event_name && (
            <span className="text-ink-muted font-normal">
              {" "}
              · {answer.event_name}
            </span>
          )}
        </p>
        {answer.note && (
          <p className="text-caption text-ink-muted">{answer.note}</p>
        )}
      </div>

      {answer.status === "attending" ? (
        <Badge tone="success">Going</Badge>
      ) : (
        <Badge tone="neutral">Can't make it</Badge>
      )}
    </div>
  );
}

function CallCard({
  call,
  organizerCount,
}: {
  call: CheckInCall;
  organizerCount: number;
}) {
  const [open, setOpen] = useState(false);
  const roster = call.roster ?? [];
  // Everyone HQ hasn't heard from either way. This is the number worth chasing.
  const silent = Math.max(organizerCount - roster.length, 0);

  return (
    <Panel
      title={`${call.title} - ${formatEventDateTime(call.starts_at)}`}
      tone={call.over ? "alt" : "brand"}
      actions={
        <ButtonLink
          href={`/admin/check-in-calls/${call.id}/edit`}
          size="sm"
          variant="secondary"
        >
          Edit
        </ButtonLink>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="success">{call.attending_count} going</Badge>
          <Badge tone="neutral">{call.declined_count ?? 0} can't</Badge>
          <Badge tone="warning">{silent} no answer</Badge>
          {!call.published && <Badge tone="danger">Hidden</Badge>}
          <span className="text-caption text-ink-soft">
            {call.duration_minutes} min
          </span>
        </div>

        {call.agenda && (
          <p className="text-body text-ink-muted whitespace-pre-line">
            {call.agenda}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {call.zoom_url ? (
            <ButtonAnchor
              size="sm"
              href={call.zoom_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {call.joinable ? "Join now" : "Call link"}
            </ButtonAnchor>
          ) : (
            <Badge tone="warning">No Zoom link yet</Badge>
          )}

          {roster.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="font-display text-caption text-ink cursor-pointer font-bold underline"
            >
              {open ? "Hide" : "Show"} who answered ({roster.length})
            </button>
          )}
        </div>

        {open && (
          <div className="flex flex-col">
            {roster.map((answer) => (
              <AnswerRow key={answer.id} answer={answer} />
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

export default function AdminCheckInCalls({
  upcoming,
  past,
  organizer_count,
}: {
  upcoming: CheckInCall[];
  past: CheckInCall[];
  organizer_count: number;
}) {
  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title="Check-in calls"
          subtitle={`The whole-program call organizers RSVP to · ${organizer_count} organizers`}
          actions={
            <ButtonLink href="/admin/check-in-calls/new">
              Schedule a call
            </ButtonLink>
          }
        />

        {upcoming.length === 0 && past.length === 0 ? (
          <EmptyState
            title="No calls yet"
            description="Schedule one and it appears on every approved organizer's dashboard."
          />
        ) : (
          <div className="flex flex-col gap-5">
            {upcoming.map((call) => (
              <CallCard
                key={call.id}
                call={call}
                organizerCount={organizer_count}
              />
            ))}
          </div>
        )}

        {past.length > 0 && (
          <div className="flex flex-col gap-5">
            <h2 className="font-display text-subheading text-ink-soft font-bold tracking-wide uppercase">
              Done
            </h2>
            {past.map((call) => (
              <CallCard
                key={call.id}
                call={call}
                organizerCount={organizer_count}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
